import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, type Path, type Resolver, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { PackageInput } from "@nh/shared";
import { Alert, Form, FormError } from "@/shared/ui/core";
import { PageSkeleton } from "@/shared/ui/ui";
import { PageHeader } from "@/shared/ui/layout";
import { isApiError } from "@/shared/api/client";
import { AdminGuard } from "@/features/admin/components";
import { PackageFormActions } from "./components/PackageFormActions";
import { PackageFormBody } from "./components/PackageFormBody";
import {
  emptyPackageFormValues,
  packageFormSchema,
  packageToFormValues,
  toPackageUpdate,
  type PackageFormValues,
} from "./packageForm";
import {
  useAdminPackage,
  useCreatePackage,
  useUpdatePackage,
} from "./packages.queries";
import { useApiErrorMessage } from "./useApiErrorMessage";

/**
 * Crear y editar un paquete.
 *
 * ── Una sola pantalla para crear y para editar ──────────────────────
 * Lo que cambia entre los dos casos es de dónde salen los valores
 * iniciales, si el slug se puede tocar y a qué endpoint va el envío.
 * Todo lo demás —los campos, la validación, los dos idiomas, la cuenta
 * del precio— es idéntico, y duplicarlo garantiza que un arreglo se
 * aplique solo en una mitad.
 *
 * ── Lo que se manda NO incluye el precio ────────────────────────────
 * `packageInputSchema` no tiene `prices` ni `listPrices`: viaja el
 * descuento y el servidor deriva el resto. Lo que la pantalla enseña
 * mientras se edita es una VISTA PREVIA de esa misma cuenta, hecha con
 * las funciones del contrato, y así está rotulada.
 */
export function AdminPackageFormPage() {
  return (
    <AdminGuard>
      <PackageFormScreen />
    </AdminGuard>
  );
}

function PackageFormScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const existing = useAdminPackage(id);
  const create = useCreatePackage();
  const update = useUpdatePackage(id ?? "");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<PackageFormValues, unknown, PackageInput>({
    /*
     * El `as` documenta una diferencia real, no la tapa.
     *
     * `zodResolver` se tipa como "recibe lo que el esquema ACEPTA", y lo
     * que este esquema devuelve tras el `.transform().pipe()` es un
     * `PackageInput` — otra forma distinta de la de los controles. La
     * varianza no encaja; el comportamiento sí, y es exactamente el que
     * hace falta: se le pasan los valores del formulario y responde con
     * los errores en las rutas de los campos.
     */
    resolver: zodResolver(packageFormSchema) as unknown as Resolver<
      PackageFormValues,
      unknown,
      PackageInput
    >,
    defaultValues: emptyPackageFormValues(),
    mode: "onSubmit",
  });

  const loadErrorMessage = useApiErrorMessage(existing.error);

  /*
   * `reset` y no `setValue` campo a campo: reset REEMPLAZA los valores
   * por omisión, así que a partir de aquí "sucio" significa "distinto de
   * lo que hay guardado" — que es lo que `PackageSlugField` consulta
   * para saber si alguien tocó el slug a mano.
   */
  useEffect(() => {
    if (existing.data) form.reset(packageToFormValues(existing.data));
  }, [existing.data, form]);

  const submit: SubmitHandler<PackageInput> = async (input) => {
    setSubmitError(null);
    try {
      if (isEdit && id) {
        await update.mutateAsync(toPackageUpdate(input));
      } else {
        await create.mutateAsync(input);
      }
      navigate("/admin/packages");
    } catch (error) {
      /*
       * El servidor valida otra vez, y sabe cosas que el navegador no:
       * que ese slug ya existe, o que un producto del paquete se
       * archivó. Cuando señala campos, el aviso va EN el campo —las
       * rutas del contrato coinciden con los nombres del formulario— y
       * no en un cartel genérico arriba que obliga a buscar cuál.
       */
      if (isApiError(error) && error.fields) {
        for (const [field, messages] of Object.entries(error.fields)) {
          const message = messages[0];
          if (!message) continue;
          form.setError(field as Path<PackageFormValues>, {
            type: "server",
            message,
          });
        }
        return;
      }

      setSubmitError(
        isApiError(error) ? error.message : t("Algo salió mal. Vuelve a intentarlo."),
      );
    }
  };

  if (isEdit && existing.isPending) {
    return <PageSkeleton />;
  }

  if (isEdit && existing.isError) {
    return (
      <Alert variant="error" title={t("No se pudo cargar el paquete")}>
        {loadErrorMessage}
      </Alert>
    );
  }

  const submitLabel = isEdit ? t("Guardar cambios") : t("Crear paquete");

  return (
    <>
      <PageHeader
        compact
        label={t("Paquetes")}
        title={isEdit ? t("Editar paquete") : t("Nuevo paquete")}
        description={
          isEdit
            ? t("Los cambios se ven en la tienda en cuanto guardas.")
            : t("Un paquete nuevo nace como borrador: nadie lo ve hasta publicarlo.")
        }
        // Sin `divider`: la card que viene justo debajo ya pone su separación, y dos
        // líneas seguidas se leen como un error de maquetación (Anahí, 26-ago).
      />

      <FormError error={submitError} onDismiss={() => setSubmitError(null)} />

      <Form form={form} onSubmit={submit} className="grid gap-5">
        <PackageFormBody locked={isEdit} />

        <PackageFormActions
          submitLabel={submitLabel}
          onCancel={() => navigate("/admin/packages")}
        />
      </Form>
    </>
  );
}
